import React from "react";

import type { IconName } from "@similarweb/icons";
import { findParentByClass, ScrollArea, ListItemMarket } from "@similarweb/ui-components";

import { MARKET_RESEARCH_BASE_PATH } from "app/pages/industry-analysis/routes/v2RouteDefinitions";
import type { ICategory } from "app/common/services/categoryService.types";
import { ECategoryType } from "app/common/services/categoryService.types";
import { i18nFilter } from "app/filters/i18nFilter";

const i18n = i18nFilter();

export const renderItemCreator = (renderItemsClick: FixmeType) => (item: FixmeType) => {
    return { ...item, props: { onClick: renderItemsClick(item) } };
};

export const renderItemsClick =
    (autocompleteRef: FixmeType, onClick: FixmeType, query: string) => (item: FixmeType) => () => {
        autocompleteRef.current.truncateResults(true);
        onClick(item, query);
    };

export const useAutoCompleteClick = (
    autocompleteRef: FixmeType,
    truncateResultsOnBodyClick = true,
    parentClassName = "AutocompleteWithTabs",
) => {
    const handleBodyClick = (e: FixmeType) => {
        if (!findParentByClass(e.target, parentClassName) && truncateResultsOnBodyClick) {
            autocompleteRef.current?.truncateResults?.(true);
        }
    };

    return React.useEffect(() => {
        document.body.addEventListener("click", handleBodyClick, {
            capture: true,
        });
        return () => {
            document.body.removeEventListener("click", handleBodyClick, {
                capture: true,
            });
        };
    }, []);
};

export const ScrollAreaWrap = ({ children, ...rest }: FixmeType) => {
    return (
        <ScrollArea
            style={{ maxHeight: 200, minHeight: 0 }}
            verticalScrollbarStyle={{ borderRadius: 5 }}
            horizontal={false}
            smoothScrolling={true}
            minScrollSize={48}
            {...rest}
        >
            {children}
        </ScrollArea>
    );
};

const resolveItemText = (
    item: ICategory & { getText?: () => string; getChildrenText?: () => string },
    isChild: boolean,
) => {
    const text = item.getText ? item.getText() : item.text;
    return isChild ? text.split(">").slice(-1)[0].trim() : text;
};

const resolveItemIcon = (item: ICategory, isChild: boolean): IconName => {
    if (isChild) {
        // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'IconName' — legacy returns null for child items; ListItemMarket treats null/undefined icon identically, and its iconName prop type excludes null
        return null;
    }

    return item.isCustomCategory ? "category" : "market";
};

export const createListItemFactory = (
    renderItemsClick,
    setCustomCategoryName,
    setShowCustomCategoriesWizard,
    customResolveItemIcon,
    params,
    disabledCategories = [],
    hasSelectedCategoryWithSegments?: boolean,
) => {
    const isCategoryDisabled = (item: FixmeType) => {
        if (!disabledCategories || disabledCategories.length === 0) {
            return false;
        }

        return disabledCategories.some((disabledCategory) => {
            const itemId = item.forUrl || item.categoryId || item.id;
            const disabledId = disabledCategory.forUrl || disabledCategory.categoryId || disabledCategory.id;
            return itemId === disabledId;
        });
    };

    const isSegmentIncompatibleWithComparison = (item: FixmeType) => {
        if (typeof hasSelectedCategoryWithSegments !== "boolean") {
            return false;
        }
        const itemHasSegments = !!item?.segments?.length;
        return itemHasSegments !== hasSelectedCategoryWithSegments;
    };

    const getTooltipText = (
        isSegmentsDisabled: boolean,
        isDisabledFromComparison: boolean,
        isSegmentIncompatible: boolean,
    ) => {
        const isMarketResearchPage = window.location.hash.includes(MARKET_RESEARCH_BASE_PATH);

        if (isSegmentsDisabled) {
            if (isMarketResearchPage) {
                return i18n("industry.overview.query.bar.dropdown.item.segments.disabled");
            }
            return i18n("digital.marketing.custom.industries.not.supported");
        }

        if (isSegmentIncompatible && isMarketResearchPage) {
            return i18n("industry.overview.query.bar.dropdown.item.segments.incompatible");
        }

        if (isDisabledFromComparison) {
            if (isMarketResearchPage) {
                return i18n("industry.overview.query.bar.dropdown.item.selected.disabled");
            }
        }

        return "";
    };

    return (enforceIcon = false, editable = true) =>
        function (item: FixmeType) {
            // if enforceIcon is true, It mean we should treat this item as a parent To ignore the left indentation
            const isChild = !enforceIcon && item.isChild;
            const isDisabledFromComparison = isCategoryDisabled(item);

            const hasSegments = !!item?.segments?.length;
            const { disableCustomIndustrySegmentsSupport } = params;
            const isSegmentsDisabled = hasSegments && disableCustomIndustrySegmentsSupport;
            const isSegmentIncompatible = isSegmentIncompatibleWithComparison(item);
            const isItemDisabled = isSegmentsDisabled || isDisabledFromComparison || isSegmentIncompatible;

            return (
                <ListItemMarket
                    isDisabled={isItemDisabled}
                    tooltipText={getTooltipText(isSegmentsDisabled, isDisabledFromComparison, isSegmentIncompatible)}
                    key={item.categoryId}
                    isActive={false}
                    text={resolveItemText(item, isChild)}
                    iconName={
                        customResolveItemIcon ? customResolveItemIcon(item, isChild) : resolveItemIcon(item, isChild)
                    }
                    onClick={isItemDisabled ? undefined : renderItemsClick(item)}
                    onEditButtonClick={
                        item.isCustomCategory && editable
                            ? () => {
                                  setCustomCategoryName(item.text);
                                  setShowCustomCategoriesWizard(true);
                              }
                            : undefined
                    }
                />
            );
        };
};

export const getPartnerTypeLists = (groups: FixmeType) => {
    return groups.filter(
        (group: FixmeType) =>
            group.CategoryType === ECategoryType.PARTNERS_LIST || group.categoryType === ECategoryType.PARTNERS_LIST,
    );
};
